export const GETTING_STARTED_GUIDE = `
# Getting Started with VoiceThera

Welcome to VoiceThera! This guide will help you understand how to use AI-generated personalized wellness sessions to transform your mental health and wellbeing.

## What is VoiceThera?

VoiceThera is an AI-powered wellness platform that creates **personalized audio meditation, mindfulness, and wellness sessions** tailored specifically to your life, challenges, and goals. Unlike generic guided meditations, every VoiceThera session is uniquely crafted for you using advanced AI technology.

### How It Works

1. **You share your story** through our Personal Biography system
2. **AI understands your context** - your challenges, strengths, relationships, and goals
3. **Choose a wellness category** - Meditation, Sleep, Anxiety Relief, ZEN AF, and more
4. **Answer a quick questionnaire** about what you need right now
5. **AI generates your session** - a personalized audio experience with your chosen voice and music
6. **Listen and transform** - use your sessions anytime, anywhere

## Sample vs Personalized Sessions

### Sample Sessions (Try Before You Personalize)

Every category includes **free sample sessions** you can listen to immediately:

- **No credits required** - Listen unlimited times
- **High-quality content** - Professional, therapeutic scripts
- **Category preview** - Understand the style and approach
- **No biography needed** - Start exploring right away

**Perfect for:** Discovering which categories resonate with you before investing in personalization.

### Personalized Sessions (AI-Generated For You)

Once you're ready to experience true personalization:

- **Uses your Personal Biography** - AI incorporates your specific life details
- **Addresses your current needs** - Based on questionnaire responses
- **Consumes credits** - 1 credit per minute of audio (5-minute session = 5 credits)
- **Unlimited replays** - Once generated, listen as many times as you want
- **Save to library** - Access all your sessions in Session History

**Perfect for:** Deep therapeutic work addressing your unique challenges and goals.

## Your First Session: Step-by-Step

### Step 1: Browse Categories

From your dashboard, explore our wellness categories:

- **Meditation** - Classic mindfulness and meditation practices
- **Anxiety & Stress Relief** - Calm your nervous system
- **Sleep** - Fall asleep faster, sleep deeper
- **Massage** - Guided body relaxation
- **Motivation** - Unlock your drive and ambition
- **Memory** - Enhance cognitive function
- **Fitness** - Mental preparation for physical performance
- **ZEN AF** - Raw, authentic, no-BS therapeutic sessions (Premium)
- **News Meditation** - Transform daily news into mindfulness practice (Premium)

### Step 2: Try a Sample Session (Optional)

Click any category and select "Sample Sessions" to:

- Listen to pre-created high-quality sessions
- Get a feel for the category's approach
- Decide if this category fits your needs
- No biography or credits needed

### Step 3: Set Up Your Biography (Recommended)

For personalized sessions, invest 10-15 minutes creating your Personal Biography:

1. Navigate to **Profile → Personal Biography**
2. Answer guided questions about:
   - Where you live and your environment
   - Your family, relationships, and support system
   - Current challenges and stressors
   - Past victories and strengths
   - Personal goals and aspirations
   - Favorite places and hobbies
3. Use the **Smart Writing Assistant** for suggestions
4. Check your **Biography Quality Score** (aim for 70%+)
5. Save your biography

**Tip:** The more detailed your biography, the more personalized and powerful your sessions become.

### Step 4: Generate Your First Personalized Session

1. Choose a category that resonates with your current needs
2. Click **"Create Personalized Session"**
3. Complete the questionnaire:
   - What's bothering you right now?
   - How are you feeling physically and emotionally?
   - What do you want to achieve in this session?
4. Select session preferences:
   - **Duration** - 1-15 minutes (more time = deeper work)
   - **Voice** - Choose a voice that feels right
   - **Background Music** - Pick your ambiance
5. Click **"Generate Session"**
6. Wait 30-60 seconds while AI creates your session
7. Listen immediately or save for later

### Step 5: Explore Your Dashboard

After your first session, explore the dashboard features:

- **Session History** - Access all your generated sessions
- **Favorites** - Save your most impactful sessions
- **Public Library** - Browse sessions shared by the community
- **Credits Balance** - Track your available minutes
- **Group Sessions** - Create sessions for teams, families, or friends

## Understanding Your Dashboard

### Main Navigation

- **Home** - Quick access to recent sessions and categories
- **Categories** - Browse all wellness session types
- **Session History** - All your personalized sessions in one place
- **Public Library** - Community-shared sessions
- **Groups** - Manage group sessions (for teams/families)
- **Profile** - Biography, preferences, and account settings
- **Credits** - Purchase additional minutes
- **Help** - Access this guide and support resources

### Quick Stats

Your dashboard displays:

- **Credits Balance** - Available minutes for new sessions
- **Recent Sessions** - Last 5 sessions you generated
- **Favorite Categories** - Most-used categories
- **Streaks** - Daily login rewards

## Best Practices for New Users

### 1. Start with Sample Sessions

Don't rush into personalized sessions. Try 2-3 sample sessions from different categories to discover what resonates.

### 2. Invest in Your Biography

A rich, detailed biography transforms session quality. Set aside 15 minutes to craft a comprehensive biography before generating personalized content.

### 3. Choose the Right Session Length

- **1-3 minutes** - Quick resets, grounding, emergency anxiety relief
- **5-7 minutes** - Standard therapeutic sessions, daily practice
- **10-15 minutes** - Deep work, complex challenges, bedtime sessions

### 4. Be Specific in Questionnaires

The more specific your answers, the better AI can personalize:

**Vague:** "I'm stressed"
**Specific:** "My boss criticized my project presentation in front of the team, and now I'm spiraling about my competence"

### 5. Create a Listening Routine

- **Morning** - Motivation or Meditation sessions
- **Midday** - Anxiety Relief or Quick Reset (1-3 minutes)
- **Evening** - Sleep or Relaxation sessions
- **As-needed** - ZEN AF for breakthrough moments

### 6. Experiment with Voices and Music

Different voices and music create different experiences. Try multiple combinations to find what works best for you.

### 7. Save Your Favorites

Mark powerful sessions as favorites so you can replay them during similar situations.

## Common Questions

**Q: Do I need to create a new session every time?**
A: No! Once generated, sessions are saved forever. Replay them as many times as you want at no additional cost.

**Q: How many credits do I need?**
A: Start with the 100-credit Starter Pack ($10). That's 100 minutes of audio - typically 15-20 full sessions.

**Q: Can I use VoiceThera without creating a biography?**
A: Yes, you can listen to unlimited sample sessions. However, personalized sessions require a biography for AI to create content specific to your life.

**Q: What if I don't like a generated session?**
A: Session quality depends on biography depth and questionnaire specificity. If a session misses the mark, refine your biography and try again with more detailed questionnaire responses.

**Q: Are sessions therapeutic?**
A: VoiceThera sessions incorporate evidence-based therapeutic approaches but are not a substitute for professional mental health treatment. They are powerful wellness tools for self-care and emotional regulation.

**Q: Can I download sessions for offline use?**
A: Offline downloads are coming soon! For now, you need an internet connection to play sessions.

## Next Steps

1. Try 2-3 sample sessions from different categories
2. Create your Personal Biography (Profile → Personal Biography)
3. Purchase your first credit pack (Credits → Buy Credits)
4. Generate your first personalized session
5. Explore advanced features (Groups, ZEN AF, News Meditation)

Welcome to your wellness journey. VoiceThera is here to support you every step of the way.

---

**Need more help?** Browse other guides in the Help Center or contact support at support@voicethera.com
`;
